// Libraries
import { Injectable, Inject } from '@angular/core';
import {
  HttpRequest,
  HttpResponse,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/do';

// Services
import { MockupService } from './mockup-service';

// Models
import {
  MOCKUP_CONFIG,
  MockupConfig
} from './interfaces/mockup-config.interface';
import { MockupResponse } from './interfaces/mockup-response.interface';
import { MockupPathConfig } from './interfaces/mockup-path-config.interface';

/**
 * `MockupInterceptor` simulates the implementation of the backend service
 *
 * @author Lautaro Klein <lautaro.klein@woopasoft.com>
 * @export
 */
@Injectable()
export class MockupInterceptor implements HttpInterceptor {

  /**
   * Creates an instance of MockupInterceptor.
   * @author Lautaro Klein <lautaro.klein@woopasoft.com>
   * @memberof MockupInterceptor
   */
  constructor(
    @Inject(MOCKUP_CONFIG) private mockupConfig: MockupConfig
  ) {
  }

  /**
   * Intercept request
   *
   * @author Lautaro Klein <lautaro.klein@woopasoft.com>
   * @memberof MockupInterceptor
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // wrap in delayed observable to simulate server api call
    return Observable.of(null).mergeMap(() => {
      // Exit if is not enabled
      if (!this.mockupConfig.enabled) {
        return next.handle(request);
      }
      // Check if error response is enabled
      if (this.mockupConfig.errorResponse) {
        // TODO: ADD DELAY
        return Observable.throw(new HttpErrorResponse(this.mockupConfig.errorResponse));
      }
      // Generate response
      const response: any = this.generateHttpResponse(request);
      // Mockup response
      if (response) {
        // Check if response has error
        if (!response.hasError) {
          // TODO: ADD DELAY
          return Observable.of(new HttpResponse(response));
        } else {
          // TODO: ADD DELAY
          return Observable.throw(new HttpErrorResponse(response));
        }
      } else {
        return next.handle(request);
      }
    });
  }

  // Private methods
  /**
   * Generate http response
   *
   * @author Lautaro Klein <lautaro.klein@woopasoft.com>
   * @memberof MockupInterceptor
   */
  private generateHttpResponse(request: HttpRequest<any>): MockupResponse | null {
    const pathObject: any = this.getPathObject(request);
    // Exit if path is not handle
    if (!pathObject) {
      return null;
    }
    // Generate response
    let service: MockupService = pathObject.service;
    let response: any;
    // Check request method
    switch (request.method) {
      case 'GET':
        response = service.handleGet ? service.handleGet(request, pathObject.pathMatch) : null;
        break;
      case 'POST':
        response = service.handlePost ? service.handlePost(request, pathObject.pathMatch) : null;
        break;
      case 'PUT':
        response = service.handlePut ? service.handlePut(request, pathObject.pathMatch) : null;
        break;
      case 'PATCH':
        response = service.handlePatch ? service.handlePatch(request, pathObject.pathMatch) : null;
        break;
      case 'DELETE':
        response = service.handleDelete ? service.handleDelete(request, pathObject.pathMatch) : null;
        break;
      case 'OPTIONS':
        response = service.handleOptions ? service.handleOptions(request, pathObject.pathMatch) : null;
        break;
    }
    return response;
  }

  /**
   * Get path object
   *
   * @author Lautaro Klein <lautaro.klein@woopasoft.com>
   * @memberof MockupInterceptor
   */
  private getPathObject(request: HttpRequest<any>): MockupPathConfig | null {
    let pathObject = null;
    let pathMatch;
    // Find if any path match with current request url
    this.mockupConfig.paths.forEach((pathObj) => {
      pathMatch = pathObj.pathRegex.exec(request.url);
      // Check if request url ends with current path
      if (pathObj.enabled && pathMatch) {
        pathObject = pathObj;
        // Parse query params
        pathObject.pathMatch = pathMatch;
      }
    });
    // Return path object
    return pathObject;
  }
}
